import { TrustGraphPanel } from '@/components/trust-graph-panel';
import { get } from '@/lib/api';
import { THREAT_CLASS_LABELS } from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';

interface WorkflowSummary {
	readonly workflowPath: string;
	readonly triggers?: readonly string[];
	readonly findings?: readonly {
		readonly detectorId: string;
		readonly severity: string;
		readonly message?: string;
		readonly threatClass?: string;
	}[];
}

export function PostureRepoDetailPage() {
	const params = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	// Splat route — React Router puts everything after /posture/repos/ into '*'
	const repoIdValue = params['*'] ?? '';
	const workflowPath = searchParams.get('workflowPath') ?? undefined;

	const { data, isLoading, error } = useQuery<{ workflows: WorkflowSummary[] }>({
		queryKey: queryKeys.posture.workflows(repoIdValue),
		queryFn: () =>
			get<{ workflows: WorkflowSummary[] }>(
				`/posture/repositories/${encodeURIComponent(repoIdValue)}/workflows`,
			),
		enabled: !!repoIdValue,
	});

	if (isLoading) return <div className="text-muted-foreground">Loading repository...</div>;
	if (error) return <div className="text-destructive">Failed to load repository details.</div>;

	const workflows = data?.workflows ?? [];

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-2">
				<Link to="/posture" className="text-sm text-primary hover:underline">
					&larr; Posture
				</Link>
			</div>

			<h1 className="text-2xl font-bold text-foreground">Repository Workflows</h1>

			<TrustGraphPanel
				query={{ scope: 'repo', repoId: repoIdValue, workflowPath }}
				emptyLabel="This repository does not have enough graph data to visualize yet."
			/>

			<div>
				{workflows.length === 0 ? (
					<p className="text-sm text-muted-foreground">No workflows found.</p>
				) : (
					<div className="space-y-4">
						{workflows.map((wf) => (
							<WorkflowCard
								key={wf.workflowPath}
								workflow={wf}
								active={workflowPath === wf.workflowPath}
								onFocus={() => {
									const next = new URLSearchParams(searchParams);
									next.set('workflowPath', wf.workflowPath);
									setSearchParams(next);
								}}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

function WorkflowCard({
	workflow,
	active,
	onFocus,
}: {
	workflow: WorkflowSummary;
	active: boolean;
	onFocus: () => void;
}) {
	const totalFindings = workflow.findings?.length ?? 0;

	return (
		<div
			className={cn(
				'rounded-lg border bg-card p-4 transition-colors',
				active ? 'border-primary/40 bg-primary/5' : 'border-border',
			)}
		>
			<div className="flex items-center justify-between">
				<h3 className="font-mono text-sm font-medium text-foreground">{workflow.workflowPath}</h3>
				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={onFocus}
						className="text-xs font-medium text-primary hover:underline"
					>
						Focus in graph
					</button>
					{totalFindings > 0 ? (
						<span className="text-xs font-medium text-severity-high">{totalFindings} findings</span>
					) : (
						<span className="text-xs text-green-600">Clean</span>
					)}
				</div>
			</div>

			{workflow.triggers && workflow.triggers.length > 0 && (
				<div className="mt-1 text-xs text-muted-foreground">
					Triggers: {workflow.triggers.join(', ')}
				</div>
			)}

			{totalFindings > 0 && workflow.findings && (
				<div className="mt-3 space-y-2">
					{workflow.findings.map((f, i) => (
						<div
							key={`${f.detectorId}-${String(i)}`}
							className="rounded border border-border bg-muted px-3 py-2"
						>
							<div className="flex items-center justify-between">
								<span className="text-sm text-foreground">{f.detectorId}</span>
								<span
									className={cn(
										'text-xs font-medium',
										f.severity === 'critical' && 'text-severity-critical',
										f.severity === 'high' && 'text-severity-high',
										f.severity === 'medium' && 'text-severity-medium',
										f.severity === 'low' && 'text-severity-low',
									)}
								>
									{f.severity}
								</span>
							</div>
							{f.message && <p className="mt-1 text-xs text-muted-foreground">{f.message}</p>}
							{f.threatClass && (
								<span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
									{THREAT_CLASS_LABELS[f.threatClass] ?? f.threatClass}
								</span>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
